import { RefObject, useEffect } from 'react';
import type { Feature } from 'geojson';

import {
  CLOSE_HIT_PX,
  DRAWING_Z_INDEX,
  LINE_COLOR,
  OUTLINE_COLOR,
  STROKE_WEIGHT,
} from '../drawingConstants';
import {
  CommittedShape,
  latLngToLiteral,
  makeVertexDot,
} from '../drawingUtilities';
import {
  DrawingMode,
  pathToLineFeature,
  pathToPolygonFeature,
} from '../geometry';

type UsePolygonDrawArgs = {
  map: google.maps.Map | null;
  // The active draw kind while armed; null when not drawing a polygon/line.
  kind: 'polygon' | 'linestring' | null;
  projectionOverlayRef: RefObject<google.maps.OverlayView | null>;
  committedRef: RefObject<CommittedShape | null>;
  makeEditable: (shape: CommittedShape['shape'], drawKind: DrawingMode) => void;
  commitNow: (feature: Feature) => void;
  lineColor?: string;
};

// Polygon / linestring draw: click to place vertices with a rubber-band
// preview, click the first/any vertex or double-click to finish. Adopts the
// finished overlay as the committed, editable shape.
const usePolygonDraw = ({
  map,
  kind,
  projectionOverlayRef,
  committedRef,
  makeEditable,
  commitNow,
  lineColor,
}: UsePolygonDrawArgs) => {
  useEffect(() => {
    if (!map || !kind) {
      return undefined;
    }

    const verts: google.maps.LatLng[] = [];
    const vertexMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
    let live: google.maps.Polygon | google.maps.Polyline | null = null;
    // Set once the shape is finished so trailing click/move/dblclick events
    // (e.g. the rest of a closing double-click) don't spawn a ghost shape.
    let done = false;

    const addVertexMarker = (position: google.maps.LatLng) => {
      vertexMarkers.push(
        new google.maps.marker.AdvancedMarkerElement({
          map,
          position,
          content: makeVertexDot(),
          gmpClickable: false,
          zIndex: DRAWING_Z_INDEX,
        }),
      );
    };

    const clearVertexMarkers = () => {
      vertexMarkers.forEach(marker => {
        marker.map = null;
      });
      vertexMarkers.length = 0;
    };

    const ensureLive = () => {
      if (live) {
        return;
      }
      live =
        kind === 'polygon'
          ? new google.maps.Polygon({
              map,
              paths: [[...verts]],
              fillOpacity: 0,
              strokeColor: OUTLINE_COLOR,
              strokeWeight: STROKE_WEIGHT,
              clickable: false,
              zIndex: DRAWING_Z_INDEX,
            })
          : new google.maps.Polyline({
              map,
              path: [...verts],
              strokeColor: lineColor ?? LINE_COLOR,
              strokeWeight: STROKE_WEIGHT,
              clickable: false,
              zIndex: DRAWING_Z_INDEX,
            });
    };

    // Render the placed vertices, optionally with a provisional vertex at the
    // cursor (the rubber-band segment that follows the mouse between clicks).
    const render = (preview?: google.maps.LatLng) => {
      if (!live) {
        return;
      }

      const points = preview ? [...verts, preview] : [...verts];
      if (kind === 'polygon') {
        (live as google.maps.Polygon).setPaths([points]);
      } else {
        (live as google.maps.Polyline).setPath(points);
      }
    };

    const finalize = () => {
      const minVerts = kind === 'polygon' ? 3 : 2;
      if (done || verts.length < minVerts) {
        return;
      }

      done = true;
      render(); // drop the rubber-band preview before committing
      clearVertexMarkers(); // the committed shape shows its own native vertices
      const literals = verts.map(latLngToLiteral);
      // Adopt the in-progress overlay as the committed (editable) shape.
      if (live) {
        committedRef.current = { kind, shape: live };
        makeEditable(live, kind);
        live = null;
      }
      commitNow(
        kind === 'polygon'
          ? pathToPolygonFeature(literals)
          : pathToLineFeature(literals),
      );
    };

    // Whether a point is on any placed vertex (so clicking it finishes the
    // shape — closing a polygon or ending a line). A polygon needs 3 points, a
    // line needs 2.
    const onAnyVertex = (latLng: google.maps.LatLng): boolean => {
      const minVerts = kind === 'polygon' ? 3 : 2;
      if (verts.length < minVerts) {
        return false;
      }

      const projection = projectionOverlayRef.current?.getProjection();
      const point = projection?.fromLatLngToContainerPixel(latLng);
      if (!projection || !point) {
        return false;
      }

      return verts.some(vertex => {
        const px = projection.fromLatLngToContainerPixel(vertex);
        return (
          !!px && Math.hypot(px.x - point.x, px.y - point.y) <= CLOSE_HIT_PX
        );
      });
    };

    const clickListener = map.addListener(
      'click',
      (event: google.maps.MapMouseEvent) => {
        if (done || !event.latLng) {
          return;
        }

        // A double click will end the drawing
        if (event.domEvent instanceof UIEvent && event.domEvent.detail > 1) {
          return;
        }

        if (onAnyVertex(event.latLng)) {
          finalize();
          return;
        }
        verts.push(event.latLng);
        addVertexMarker(event.latLng);
        ensureLive();
        render();
      },
    );

    // Live rubber-band + a pointer cursor when hovering a vertex that would
    // close the polygon (toggled only on change to avoid per-frame setOptions).
    let overVertex = false;
    const moveListener = map.addListener(
      'mousemove',
      (event: google.maps.MapMouseEvent) => {
        if (done || !event.latLng || verts.length === 0) {
          return;
        }

        const canClose = onAnyVertex(event.latLng);
        if (canClose !== overVertex) {
          overVertex = canClose;
          map.setOptions({
            draggableCursor: canClose ? 'pointer' : 'crosshair',
          });
        }

        ensureLive();
        render(event.latLng);
      },
    );

    const dblclickListener = map.addListener('dblclick', finalize);

    return () => {
      clickListener.remove();
      moveListener.remove();
      dblclickListener.remove();
      // An incomplete in-progress shape is discarded.
      clearVertexMarkers();
      live?.setMap(null);
    };
  }, [
    map,
    kind,
    projectionOverlayRef,
    committedRef,
    makeEditable,
    commitNow,
    lineColor,
  ]);
};

export default usePolygonDraw;
